import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import Icons from '../../../../../common/widgets/Icons';
import { CustomerInfoContext } from '../../../contexts/CustomerInfoContext';
import { BuyerTypes } from '../../../enums';
import {
  BrandFilterType,
  BuyerTypeFilter,
  CountyFilter,
  TaxFilter,
} from '../../../models/Filters';
import { PriceRange } from '../../../models/PriceRange';
import { ProductListModel } from '../../../models/Product';
import { resources } from '../../../resources/resources';
import { FilterSelectionsState } from '../../CategoryPage/types';
import AvailableInFilter from './AvailableInFilter';
import BrandFilter from './BrandFilter';
import BuyerTypeFilterComponent from './BuyerTypeFilter';
import PriceRangeFilter from './PriceRangeFilter';
import ProductsOnOfferFilter from './ProductsOnOfferFilter';
import TaxationLevelFilter from './TaxationLevelFilter';

interface FilterPanelProps {
  availableInList?: CountyFilter[];
  brandList?: BrandFilterType[];
  buyerTypeList?: BuyerTypeFilter[];
  taxationLevelList?: TaxFilter[];
  employerTaxationLevelList?: TaxFilter[];
  priceRange?: PriceRange;
  discountedProdutsCount: number;
  prevSelectionState: FilterSelectionsState;
  hasActiveFilters?: boolean;
  handlePriceRangeFilterChanged: (range: PriceRange) => void;
  handleAvailableInFilterChanged: (countyIds: number[]) => void;
  handleBrandFilterChanged: (brandIds: number[]) => void;
  handleBuyerTypeFilterChanged: (buyerTypes: BuyerTypes[]) => void;
  handleTaxFilterChanged: (taxClassIds: number[]) => void;
  handleEmployerTaxFilterChanged: (taxClassIds: number[]) => void;
  handleProductsOnOfferFilterChanged: (isSelected: boolean) => void;
  handleClearFilters?: () => void;
  filteredProducts?: ProductListModel[];
}

export default function FilterPanel(props: Readonly<FilterPanelProps>) {
  const {
    availableInList,
    brandList,
    buyerTypeList,
    taxationLevelList,
    employerTaxationLevelList,
    priceRange,
    discountedProdutsCount,
    prevSelectionState,
    hasActiveFilters,
    handlePriceRangeFilterChanged,
    handleAvailableInFilterChanged,
    handleBrandFilterChanged,
    handleBuyerTypeFilterChanged,
    handleTaxFilterChanged,
    handleEmployerTaxFilterChanged,
    handleProductsOnOfferFilterChanged,
    handleClearFilters,
    filteredProducts,
  } = props;
  const { customerInfo } = useContext(CustomerInfoContext);
  const { t: translate } = useTranslation();

  const [searchParams] = useSearchParams();
  const brandId = searchParams.get('brand');

  const showProductsOnOfferFilter = () => discountedProdutsCount > 0;

  const showTaxationFilter = () => {
    if (customerInfo.isGrossUpUser) return false;
    if (!taxationLevelList || taxationLevelList.length === 1) return false;

    return new Set(taxationLevelList.map(it => it.taxClassId)).size > 1;
  };

  const showEmployerTaxationFilter = () => {
    if (customerInfo.isGrossUpUser) return false;
    if (!employerTaxationLevelList || employerTaxationLevelList.length === 1)
      return false;

    return new Set(employerTaxationLevelList.map(it => it.taxClassId)).size > 1;
  };

  const showAvailableFilter = () => !!availableInList?.length;

  const showBrandFilter = () => {
    if (brandId) {
      return false;
    }
    return !!brandList?.length && brandList.length > 1;
  };

  const showBuyerTypeFilter = () =>
    !!buyerTypeList?.length && buyerTypeList.length > 1;

  const showPriceRangeFilter = () =>
    priceRange && priceRange.min !== priceRange.max;

  return (
    <>
      {hasActiveFilters && (
        <button
          type="button"
          className="button-panel"
          onClick={handleClearFilters}
        >
          <span>{translate(resources.searchPage.clearFilters)}</span>
          <span className="button-panel-icon">
            <Icons.ClearFilters />
          </span>
        </button>
      )}
      {showProductsOnOfferFilter() && (
        <ProductsOnOfferFilter
          isPrevSelected={prevSelectionState.showDiscountedProducts}
          productOnOffer={{ count: discountedProdutsCount }}
          handleProductsOnOfferFilterChanged={
            handleProductsOnOfferFilterChanged
          }
        />
      )}
      {showTaxationFilter() && (
        <TaxationLevelFilter
          prevSelectedTaxClassIds={prevSelectionState.selectedTaxClassIds}
          taxationLevelList={taxationLevelList}
          handleTaxFilterChanged={handleTaxFilterChanged}
          title={translate(resources.filters.taxationLevelTitle)}
        />
      )}
      {showEmployerTaxationFilter() && (
        <TaxationLevelFilter
          prevSelectedTaxClassIds={
            prevSelectionState.selectedEmployerTaxClassIds
          }
          taxationLevelList={employerTaxationLevelList}
          handleTaxFilterChanged={handleEmployerTaxFilterChanged}
          title={translate(resources.filters.employerTaxationLevelTitle)}
        />
      )}
      {showAvailableFilter() && (
        <AvailableInFilter
          prevSelectedCountyIds={prevSelectionState.selectedCountyIds}
          availabelInList={availableInList!}
          handleAvailableInFilterChanged={handleAvailableInFilterChanged}
        />
      )}
      {showBrandFilter() && (
        <BrandFilter
          prevSelectedBrandIds={prevSelectionState.selectedBrandIds}
          brandList={brandList!}
          handleBrandFilterChanged={handleBrandFilterChanged}
        />
      )}
      {showBuyerTypeFilter() && (
        <BuyerTypeFilterComponent
          prevSelectedBuyerTypes={prevSelectionState.selectedBuyerTypes}
          buyerTypeList={buyerTypeList!}
          handleBuyerTypeFilterChanged={handleBuyerTypeFilterChanged}
        />
      )}
      {showPriceRangeFilter() && (
        <PriceRangeFilter
          priceRange={priceRange!}
          handlePriceRangeFilterChanged={handlePriceRangeFilterChanged}
          prevRangeSelected={prevSelectionState.priceRange}
          products={filteredProducts}
        />
      )}
    </>
  );
}
